import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { DatePipe } from '@angular/common';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { ReservationAddComponent } from './components/reservation/reservation-add/reservation-add.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavComponent } from './components/nav/nav.component';
import { UserDetailComponent } from './components//user/user-detail/user-detail.component';
import { MaterialModule } from './material.module';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReservationListComponent } from './components/reservation/reservation-list/reservation-list.component';
import { InstructorListComponent } from './components//instructors/instructor-list/instructor-list.component';
import { InstructorEditComponent } from './components/instructors/instructor-edit/instructor-edit.component';
import { ReservationProcessComponent } from './components/reservation/reservation-process/reservation-process.component';

import { ReservationService } from './service/reservation.service';
import { InstructorService } from './service/instructor.service';
import { InstructorDetailComponent } from './components/instructors/instructor-detail/instructor-detail.component';
import { DeleteConfirmationDialog } from './components/delete-confirmation/delete-confirmation.dialog';
import { DatePeriodEditComponent } from './components/instructors/date-period-edit/date-period-edit.component';
import { ChangeStatusDialogComponent } from './components/change-status-dialog/change-status-dialog.component';
import { LoginComponent } from './components/login/login.component';
import { AuthService } from './service/auth.service';
import { AuthGuard } from './guards/auth.guard';
import { ShowMessageDialogComponent } from './components/show-message-dialog/show-message-dialog.component';
import { SidenavComponent } from './components/nav/sidenav/sidenav.component';
import { LessonTypeComponent } from './components/instructors/lesson-type/lesson-type.component';
import { SportTypeComponent } from './components/instructors/sport-type/sport-type.component';
import { DateSlotsComponent } from './components/instructors/date-slots/date-slots.component';
import { CompanyDetailsComponent } from './components/company-details/company-details.component';
import { UserEditComponent } from './components/user/user-edit/user-edit.component';
import { RegisterComponent } from './components/user/register/register.component';
import { ScheduleInstructorComponent } from './components/schedule/schedule-instructor/schedule-instructor.component';

import { CalendarModule, DateAdapter, CalendarNativeDateFormatter, DateFormatterParams, CalendarDateFormatter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { LessonDetailComponent } from './components/lesson/lesson-detail/lesson-detail.component';
import { ScheduleComponent } from './components/schedule/schedule/schedule.component';
import { ReservationItemsComponent } from './components/reservation/reservation-items/reservation-items.component';
import { LessonSelectionComponent } from './components/lesson/lesson-selection/lesson-selection.component';
import { UserListComponent } from './components/user/user-list/user-list.component';
import { TermsAndConditionsComponent } from './components/termsAndConditions/terms-and-conditions/terms-and-conditions.component';
import { ErrorInterceptor } from './helpers/error.interceptor';
import { JwtInterceptor } from './helpers/jwt.interceptor';
import { HomeComponent } from './components/home/home.component';
import { RecaptchaModule, RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { MAT_DATE_LOCALE } from '@angular/material';
import { BooleanPipe } from 'src/app/pipes/boolean-pipe';
import { PricelistComponent } from './components/pricelist/pricelist/pricelist.component';
import { PricelistGearComponent } from './components/pricelist/pricelist-gear/pricelist-gear.component';
import { ProductListComponent } from './components/products/product-list/product-list.component';
import { PaymentFinishComponent } from './components/payment/payment-finish/payment-finish.component';
import { PaymentInitComponent } from './components/payment/payment-init/payment-init.component';

import {TableModule} from 'primeng/table';
import {ButtonModule} from 'primeng/button';
import {InputTextModule} from 'primeng/inputtext';
import { TranslationPipe } from './pipes/translation.pipe';
import { TranslationService } from './service/translate.service';
import { OrderDetailsComponent } from './components/payment/order-details/order-details.component';
import { EquipmentAddComponent } from './components/equipment/equipment-add/equipment-add.component';
import { ScheduleEquipmentComponent } from './components/schedule/schedule-equipment/schedule-equipment.component';


class CustomDateFormatter extends CalendarNativeDateFormatter {

  public dayViewHour({date, locale}: DateFormatterParams): string {
    return new Intl.DateTimeFormat('sr-Latn', {
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  }
}


@NgModule({
  declarations: [
    AppComponent,
    ReservationAddComponent,
    NavComponent,
    UserDetailComponent,
    ReservationListComponent,
    InstructorListComponent,
    InstructorEditComponent,
    ReservationProcessComponent,
    InstructorDetailComponent,
    DeleteConfirmationDialog,
    DatePeriodEditComponent,
    ChangeStatusDialogComponent,
    ShowMessageDialogComponent,
    LoginComponent,
    SidenavComponent,
    LessonTypeComponent,
    SportTypeComponent,
    DateSlotsComponent,
    CompanyDetailsComponent,
    UserEditComponent,
    RegisterComponent,
    ScheduleInstructorComponent,
    LessonDetailComponent,
    ScheduleComponent,
    ReservationItemsComponent,
    LessonSelectionComponent,
    UserListComponent,
    TermsAndConditionsComponent,
    HomeComponent,
    BooleanPipe,
    TranslationPipe,
    PricelistComponent,
    PricelistGearComponent,
    ProductListComponent,
    PaymentFinishComponent,
    PaymentInitComponent,
    OrderDetailsComponent,
    EquipmentAddComponent,
    ScheduleEquipmentComponent
  ],
  entryComponents: [
    DeleteConfirmationDialog,
    DatePeriodEditComponent,
    ChangeStatusDialogComponent,
    ShowMessageDialogComponent,
    LessonDetailComponent,
    LessonSelectionComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory
    },
      {
        dateFormatter: {
          provide: CalendarDateFormatter,
          useClass: CustomDateFormatter,
        }
      }
    ),
    FlexLayoutModule,
    RecaptchaModule.forRoot(),
    TableModule,
    ButtonModule,
    InputTextModule
  ],
  providers: [
    { provide: CalendarDateFormatter, useClass: CustomDateFormatter},
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'sr-Latn'},
    ReservationService, InstructorService, AuthService, AuthGuard, DatePipe, BooleanPipe, TranslationPipe, TranslationService],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
